import type Uppy from "@uppy/core"
import type { Body, Meta } from "@uppy/core"
import { onBeforeUnmount, readonly, shallowRef } from "vue"

export type ImageDestination = "container" | "child"
export type ImageUploadMeta = Meta & { destination?: ImageDestination }

interface PromptFile {
  id: string
  name: string
  previewUrl: string
}

export function useContainerImageDestinationPrompt<B extends Body>(
  uppy: Uppy<ImageUploadMeta, B>,
  enabled: boolean,
) {
  const promptFile = shallowRef<PromptFile | null>(null)
  let resolveDestination: ((destination: ImageDestination) => void) | undefined

  async function prepareUploads(fileIds: string[]) {
    for (const fileId of fileIds) {
      const file = uppy.getFile(fileId)
      const previewUrl = file.data instanceof Blob ? URL.createObjectURL(file.data) : ""

      promptFile.value = { id: file.id, name: file.name ?? "Photo", previewUrl }
      const destination = await new Promise<ImageDestination>((resolve) => {
        resolveDestination = resolve
      })
      uppy.setFileMeta(fileId, { destination })

      if (previewUrl) URL.revokeObjectURL(previewUrl)
      promptFile.value = null
    }
  }

  function chooseDestination(destination: ImageDestination) {
    const resolve = resolveDestination
    resolveDestination = undefined
    resolve?.(destination)
  }

  if (enabled) uppy.addPreProcessor(prepareUploads)

  onBeforeUnmount(() => {
    if (enabled) uppy.removePreProcessor(prepareUploads)
    if (promptFile.value?.previewUrl) URL.revokeObjectURL(promptFile.value.previewUrl)
  })

  return {
    promptFile: readonly(promptFile),
    chooseDestination,
  }
}
